/**
 * Page préférences découverte — /discovery/preferences
 * GET/PATCH /api/discovery/preferences/
 * Formulaire : location, catégories, gratuit/payant, quotas, niveau de découverte.
 */

import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Box, Button, Alert, CircularProgress, Checkbox, FormControlLabel } from '@mui/material'
import './Discovery.css'
import {
  getDiscoveryPreferences,
  patchDiscoveryPreferences,
  getDiscoveryCategories,
} from '../../utils/discoveryApi'

const Preferences = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [prefs, setPrefs] = useState(null)
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const [prefsRes, catsRes] = await Promise.all([
          getDiscoveryPreferences(),
          getDiscoveryCategories(),
        ])
        if (prefsRes?.status === 200) {
          setPrefs(prefsRes.data)
        }
        if (catsRes?.status === 200 && catsRes.data) {
          // Organiser en parents/enfants
          setCategories(catsRes.data)
        }
      } catch (err) {
        console.error('Failed to load preferences:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const handleChange = (field, value) => {
    setPrefs((p) => ({ ...p, [field]: value }))
  }

  const handleCategoryToggle = (code, isIncluded) => {
    const current = prefs.preferred_categories || []
    const updated = isIncluded
      ? [...new Set([...current, code])]
      : current.filter((c) => c !== code)
    handleChange('preferred_categories', updated)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const res = await patchDiscoveryPreferences(prefs)
      if (res?.status === 200) {
        setMessage({ type: 'success', text: t('discovery.prefsUpdated') })
        // Le backend a géocodé la ville et publié les premières cartes
        // (published_now) : on referme la page vers le feed, où la carte
        // d'onboarding disparaît et les suggestions prennent le relais.
        setTimeout(() => navigate('/'), 900)
      } else {
        setMessage({ type: 'error', text: t('discovery.errorUpdating') })
      }
    } catch (err) {
      console.error('Save failed:', err)
      setMessage({ type: 'error', text: t('discovery.errorUpdating') })
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box className="dsc-page">
        <CircularProgress />
      </Box>
    )
  }

  if (!prefs) {
    return (
      <Box className="dsc-page">
        <Alert severity="error">{t('discovery.errorLoading')}</Alert>
      </Box>
    )
  }

  return (
    <Box className="dsc-page dsc-preferences">
      <Box className="dsc-header">
        <h1>{t('discovery.preferences')}</h1>
      </Box>

      {message && (
        <Alert severity={message.type} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Box className="dsc-form">
        {/* Enabled */}
        <Box className="dsc-field">
          <FormControlLabel
            control={
              <Checkbox
                checked={prefs.enabled}
                onChange={(e) => handleChange('enabled', e.target.checked)}
              />
            }
            label={t('discovery.enableDiscovery')}
          />
        </Box>

        {/* Location Mode */}
        <Box className="dsc-field">
          <label>{t('discovery.locationMode')}</label>
          <select
            value={prefs.location_mode || 'HOME'}
            onChange={(e) => handleChange('location_mode', e.target.value)}
            className="dsc-select"
          >
            <option value="PRECISE">{t('discovery.locationPrecise')}</option>
            <option value="APPROXIMATE">{t('discovery.locationApproximate')}</option>
            <option value="HOME">{t('discovery.locationHome')}</option>
            <option value="MANUAL">{t('discovery.locationManual')}</option>
            <option value="DISABLED">{t('discovery.locationDisabled')}</option>
          </select>
        </Box>

        {/* Home Location */}
        {prefs.location_mode !== 'DISABLED' && (
          <>
            <Box className="dsc-field">
              <label>{t('discovery.homeCity')}</label>
              <input
                type="text"
                value={prefs.home_city || ''}
                onChange={(e) => handleChange('home_city', e.target.value)}
                className="dsc-input"
              />
            </Box>

            <Box className="dsc-field">
              <label>{t('discovery.homeRadius')}</label>
              <input
                type="number"
                min="1"
                max="200"
                value={prefs.home_radius_km || 25}
                onChange={(e) => handleChange('home_radius_km', parseFloat(e.target.value))}
                className="dsc-input"
              />
              <small>km</small>
            </Box>
          </>
        )}

        {/* Pricing */}
        <Box className="dsc-field">
          <FormControlLabel
            control={
              <Checkbox
                checked={prefs.include_free_events}
                onChange={(e) => handleChange('include_free_events', e.target.checked)}
              />
            }
            label={t('discovery.includeFreeEvents')}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={prefs.include_paid_events}
                onChange={(e) => handleChange('include_paid_events', e.target.checked)}
              />
            }
            label={t('discovery.includePaidEvents')}
          />
        </Box>

        <Box className="dsc-field">
          <label>{t('discovery.maxPrice')}</label>
          <input
            type="number"
            min="0"
            value={prefs.max_price || ''}
            onChange={(e) => handleChange('max_price', e.target.value ? parseFloat(e.target.value) : null)}
            className="dsc-input"
            placeholder={t('discovery.noLimit')}
          />
        </Box>

        {/* Quotas */}
        <Box className="dsc-field">
          <label>{t('discovery.maxPerDay')}</label>
          <input
            type="number"
            min="0"
            max="10"
            value={prefs.max_recommendations_per_day || 1}
            onChange={(e) => handleChange('max_recommendations_per_day', parseInt(e.target.value))}
            className="dsc-input"
          />
        </Box>

        <Box className="dsc-field">
          <label>{t('discovery.maxPerWeek')}</label>
          <input
            type="number"
            min="0"
            max="30"
            value={prefs.max_recommendations_per_week || 4}
            onChange={(e) => handleChange('max_recommendations_per_week', parseInt(e.target.value))}
            className="dsc-input"
          />
        </Box>

        {/* Discovery Level */}
        <Box className="dsc-field">
          <label>{t('discovery.discoveryLevel')}</label>
          <select
            value={prefs.discovery_level || 'BALANCED'}
            onChange={(e) => handleChange('discovery_level', e.target.value)}
            className="dsc-select"
          >
            <option value="STRICT">{t('discovery.levelStrict')}</option>
            <option value="BALANCED">{t('discovery.levelBalanced')}</option>
            <option value="EXPLORATORY">{t('discovery.levelExploratory')}</option>
          </select>
        </Box>

        {/* Categories */}
        <Box className="dsc-field">
          <label>{t('discovery.preferredCategories')}</label>
          <Box className="dsc-categories">
            {categories.map((cat) => {
              const isTop = !cat.parent_code
              return (
                <div key={cat.code} className={`dsc-category-item ${isTop ? 'dsc-category-parent' : ''}`}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={(prefs.preferred_categories || []).includes(cat.code)}
                        onChange={(e) => handleCategoryToggle(cat.code, e.target.checked)}
                      />
                    }
                    label={cat.label}
                  />
                </div>
              )
            })}
          </Box>
        </Box>

        {/* Awareness toggles */}
        <Box className="dsc-field">
          <FormControlLabel
            control={
              <Checkbox
                checked={prefs.calendar_awareness_enabled}
                onChange={(e) => handleChange('calendar_awareness_enabled', e.target.checked)}
              />
            }
            label={t('discovery.calendarAwareness')}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={prefs.goal_matching_enabled}
                onChange={(e) => handleChange('goal_matching_enabled', e.target.checked)}
              />
            }
            label={t('discovery.goalMatching')}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={prefs.interest_matching_enabled}
                onChange={(e) => handleChange('interest_matching_enabled', e.target.checked)}
              />
            }
            label={t('discovery.interestMatching')}
          />
        </Box>
      </Box>

      <Box className="dsc-actions">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="dsc-action-btn dsc-action-primary"
        >
          {saving ? t('labels.loading') : t('buttons.save')}
        </Button>
      </Box>
    </Box>
  )
}

export default Preferences
